import { useEffect, useMemo, useRef, useState } from "react";
import apiClient from "@lift-admin/utils/apiClient";
import {
  Button,
  Input,
  Label,
  Checkbox as LiftCheckbox,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SingleSelect,
  MultiSelectCheckboxTag,
  Toastify,
} from "lift-ui";
import { cn } from "@/lib/utils";
import {
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Layers,
  Package,
  Plus,
} from "lucide-react";
import {
  slugFromAliasEmail,
  findOrganizationIdByName,
  parseCreatedOrganizationId,
  getWizardStep2Errors,
  getWizardStep3Errors,
  WIZARD_PRODUCT_FEATURES,
  type AddBrandWizardResult,
  type WizardOrgOption,
  type WizardProductFeature,
} from "./addBrandWizardUtils";
import {
  GrapevineSlugAvailabilityMessage,
  grapevineSlugSubmitError,
  useGrapevineSlugAvailability,
} from "./useGrapevineSlugAvailability";

export type OrgOption = WizardOrgOption;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizations: OrgOption[];
  onOrganizationsRefresh: () =>
    | void
    | Promise<void>
    | Promise<OrgOption[]>;
  verticalOptions: string[];
  typeOptions: { label: string; value: string }[];
  onSave: (result: AddBrandWizardResult) => Promise<void>;
};

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="text-sm text-red-500" role="alert" data-wizard-error>
      {message}
    </p>
  );
}

const STEPS = [
  { id: 1, title: "Organization", icon: Building2 },
  { id: 2, title: "Brand details", icon: Layers },
  { id: 3, title: "Brand Features", icon: Package },
] as const;

type WizardStep = 1 | 2 | 3;

export function AddBrandWizard({
  open,
  onOpenChange,
  organizations,
  onOrganizationsRefresh,
  verticalOptions,
  typeOptions,
  onSave,
}: Props) {
  const [step, setStep] = useState<WizardStep>(1);
  const [saving, setSaving] = useState(false);
  // Inline errors only appear after the user touches a field or tries to continue.
  const [showErrors, setShowErrors] = useState<Record<WizardStep, boolean>>({
    1: false,
    2: false,
    3: false,
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedOrgDisplayName, setSelectedOrgDisplayName] = useState("");
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [creatingOrg, setCreatingOrg] = useState(false);

  const [name, setName] = useState("");
  const [alias, setAlias] = useState("");
  const [domain, setDomain] = useState("");
  const [vertical, setVertical] = useState<string[]>([]);
  const [brandType, setBrandType] = useState("");

  const [productFeatures, setProductFeatures] = useState<
    Set<WizardProductFeature>
  >(new Set());
  const [isPerformanceCreator, setIsPerformanceCreator] = useState(false);
  const [hubspotCompanyId, setHubspotCompanyId] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const { status: slugStatus, resolvedSlug } = useGrapevineSlugAvailability(
    open && isPerformanceCreator ? slug : "",
  );
  const [gvVertical, setGvVertical] = useState("");
  const [gvContactEmail, setGvContactEmail] = useState("");
  const [gvContactName, setGvContactName] = useState("");
  const [gvHomepageUrl, setGvHomepageUrl] = useState("");
  const [gvTier, setGvTier] = useState("");

  useEffect(() => {
    if (!open) {
      setStep(1);
      setShowErrors({ 1: false, 2: false, 3: false });
      setTouched({});
      setSelectedOrgId("");
      setSelectedOrgDisplayName("");
      setShowCreateOrg(false);
      setNewOrgName("");
      setName("");
      setAlias("");
      setDomain("");
      setVertical([]);
      setBrandType("");
      setProductFeatures(new Set());
      setIsPerformanceCreator(false);
      setHubspotCompanyId("");
      setSlug("");
      setSlugTouched(false);
      setGvVertical("");
      setGvContactEmail("");
      setGvContactName("");
      setGvHomepageUrl("");
      setGvTier("");
    }
  }, [open]);

  useEffect(() => {
    if (!isPerformanceCreator || slugTouched) return;
    setSlug(slugFromAliasEmail(alias));
  }, [alias, isPerformanceCreator, slugTouched]);

  const activeOrgs = useMemo(
    () => organizations.filter((o) => o.is_active),
    [organizations],
  );

  const selectedOrgLabel =
    activeOrgs.find((o) => String(o.value) === selectedOrgId)?.label ??
    selectedOrgDisplayName;

  const productList = useMemo(
    () => Array.from(productFeatures),
    [productFeatures],
  );

  const canAdvanceStep1 = selectedOrgId !== "";

  const step2Errors = getWizardStep2Errors({
    name,
    alias,
    domain,
    vertical,
    type: brandType,
  });

  const step3Errors = getWizardStep3Errors(
    productList,
    isPerformanceCreator,
    hubspotCompanyId,
    {
      slug,
      vertical: gvVertical,
      contactName: gvContactName,
      contactEmail: gvContactEmail,
      homepageUrl: gvHomepageUrl,
      tier: gvTier,
    },
  );

  const step1Error = canAdvanceStep1 ? undefined : "Select or create an organization";

  /** Error for a field, once the user touched it or tried to continue. */
  const fieldError = (
    stepId: WizardStep,
    key: string,
    message: string | undefined,
  ): string | undefined =>
    message && (showErrors[stepId] || touched[key]) ? message : undefined;

  const markTouched = (key: string) =>
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  const revealErrors = (stepId: WizardStep) => {
    setShowErrors((prev) => ({ ...prev, [stepId]: true }));
    // Wait for the error messages to render, then bring the first one into view.
    setTimeout(() => {
      scrollRef.current
        ?.querySelector("[data-wizard-error]")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const toggleProduct = (key: WizardProductFeature, checked: boolean) => {
    setProductFeatures((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const selectOrganization = (orgId: string, displayName: string) => {
    setSelectedOrgId(orgId);
    setSelectedOrgDisplayName(displayName);
    setShowCreateOrg(false);
    setNewOrgName("");
  };

  const createOrganization = async (): Promise<string | null> => {
    const trimmed = newOrgName.trim();
    if (!trimmed) {
      Toastify("warning", "Organization name is required");
      return null;
    }
    setCreatingOrg(true);
    try {
      const formData = new FormData();
      formData.append("name", trimmed);
      formData.append("is_active", "true");
      const res = await apiClient.post(`/brand/v1/organizations`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const refreshed = await onOrganizationsRefresh();
      const orgList = Array.isArray(refreshed) ? refreshed : organizations;
      const createdId =
        parseCreatedOrganizationId(res.data) ??
        findOrganizationIdByName(orgList, trimmed);
      if (createdId != null) {
        const id = String(createdId);
        selectOrganization(id, trimmed);
        Toastify("success", `"${trimmed}" is ready — continue to brand details.`);
        return id;
      }
      Toastify(
        "warning",
        "Organization was created but could not be selected automatically — pick it from the list.",
      );
      setShowCreateOrg(false);
      return null;
    } catch (error: any) {
      Toastify(
        "error",
        error?.response?.data?.message || "Could not create organization",
      );
      return null;
    } finally {
      setCreatingOrg(false);
    }
  };

  const goNext = async () => {
    if (step === 1) {
      if (showCreateOrg && newOrgName.trim() && !selectedOrgId) {
        Toastify(
          "warning",
          'Click "Create organization" first, or select an existing one.',
        );
        return;
      }
      if (!canAdvanceStep1) {
        revealErrors(1);
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (Object.keys(step2Errors).length > 0) {
        revealErrors(2);
        return;
      }
      setStep(3);
    }
  };

  const goBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const finish = async () => {
    if (!selectedOrgId) {
      Toastify("warning", "Organization of Brand is required");
      return;
    }
    if (Object.keys(step3Errors).length > 0) {
      revealErrors(3);
      return;
    }
    const slugError = grapevineSlugSubmitError(
      isPerformanceCreator,
      slug,
      slugStatus,
    );
    if (slugError) {
      Toastify("warning", slugError);
      return;
    }

    const result: AddBrandWizardResult = {
      organizationId: selectedOrgId,
      organizationLabel: selectedOrgLabel,
      name: name.trim(),
      alias: alias.trim(),
      domain: domain.trim(),
      vertical,
      type: brandType,
      productFeatures: productList,
      isPerformanceCreator,
      hubspotCompanyId: hubspotCompanyId.trim(),
      gvSlug: slug.trim(),
      gvVertical: gvVertical.trim(),
      gvContactName: gvContactName.trim(),
      gvContactEmail: gvContactEmail.trim(),
      gvHomepageUrl: gvHomepageUrl.trim(),
      gvTier: gvTier.trim(),
    };

    setSaving(true);
    try {
      await onSave(result);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[640px] sm:max-w-[640px] flex flex-col h-full"
      >
        <SheetHeader>
          <SheetTitle>Add brand</SheetTitle>
          <p className="text-sm text-muted-foreground pr-6">
            Step-by-step setup for any brand type: organization first, then core
            details, then brand features. Grapevine fields appear only
            if you choose Performance Creator.
          </p>
        </SheetHeader>

        <div className="flex items-center gap-1 py-4 border-b">
          {STEPS.map((s, index) => (
            <div key={s.id} className="flex items-center gap-1 flex-1 min-w-0">
              <div
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-medium",
                  step === s.id
                    ? "border-primary bg-primary text-primary-foreground"
                    : step > s.id
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-muted-foreground/30 text-muted-foreground",
                )}
              >
                {step > s.id ? <Check className="h-4 w-4" /> : s.id}
              </div>
              <span
                className={cn(
                  "text-xs font-medium truncate hidden sm:inline",
                  step === s.id ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {s.title}
              </span>
              {index < STEPS.length - 1 && (
                <div className="h-px flex-1 bg-border mx-0.5" />
              )}
            </div>
          ))}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto py-4 space-y-6">
          {step === 1 && (
            <>
              <p className="text-sm text-muted-foreground">
                Choose an organization from the list (use the dropdown search), or
                create a new one below. You must have one selected before
                continuing.
              </p>

              {selectedOrgId && (
                <div
                  className="flex items-start gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-3"
                  role="status"
                >
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
                  >
                    <Check className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="text-sm font-medium">Organization selected</p>
                    <p className="text-sm truncate">{selectedOrgLabel}</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => {
                      setSelectedOrgId("");
                      setSelectedOrgDisplayName("");
                    }}
                  >
                    Change
                  </Button>
                </div>
              )}

              <div className="space-y-2">
                <Label>Organization *</Label>
                <SingleSelect
                  key={`wizard-org-${selectedOrgId}-${organizations.length}`}
                  options={activeOrgs.map((org) => ({
                    label: org.label,
                    value: String(org.value),
                  }))}
                  defaultValue={selectedOrgId}
                  onChange={(value) => {
                    const org = activeOrgs.find(
                      (o) => String(o.value) === value,
                    );
                    if (org) {
                      selectOrganization(value, org.label);
                    } else {
                      setSelectedOrgId(value);
                    }
                  }}
                  placeholder={
                    activeOrgs.length
                      ? "Select organization"
                      : "No organizations to show"
                  }
                  containerClassName="w-full"
                />
                <FieldError message={fieldError(1, "org", step1Error)} />
              </div>

              <div className="rounded-md border">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium hover:bg-muted/50"
                  onClick={() => setShowCreateOrg((open) => !open)}
                  aria-expanded={showCreateOrg}
                >
                  <span className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    Create new organization
                  </span>
                  <ChevronRight
                    className={cn(
                      "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                      showCreateOrg && "rotate-90",
                    )}
                  />
                </button>
                {showCreateOrg && (
                  <div className="space-y-3 border-t px-4 py-4">
                    <p className="text-xs text-muted-foreground">
                      Creates an active organization and selects it for this
                      brand. You can continue to brand details right after.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="wizard-new-org-name">Organization name *</Label>
                      <Input
                        id="wizard-new-org-name"
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        placeholder="e.g. Acme Corp"
                        disabled={creatingOrg}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void createOrganization();
                          }
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={creatingOrg || !newOrgName.trim()}
                      onClick={() => void createOrganization()}
                    >
                      {creatingOrg ? "Creating…" : "Create organization"}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="rounded-md bg-muted/50 px-3 py-2 text-sm">
                Organization:{" "}
                <span className="font-medium">{selectedOrgLabel}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Core fields required for all brands (same as the main add form).
              </p>
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={() => markTouched("name")}
                    placeholder="Eg. - LT Partners"
                  />
                  <FieldError message={fieldError(2, "name", step2Errors.name)} />
                </div>
                <div className="space-y-2">
                  <Label>Alias *</Label>
                  <Input
                    value={alias}
                    onChange={(e) => setAlias(e.target.value)}
                    onBlur={() => markTouched("alias")}
                    placeholder="Eg. - lift@newengen.com"
                  />
                  <FieldError message={fieldError(2, "alias", step2Errors.alias)} />
                </div>
                <div className="space-y-2">
                  <Label>Domain *</Label>
                  <Input
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    onBlur={() => markTouched("domain")}
                    placeholder="Eg. - newengen.com"
                  />
                  <FieldError message={fieldError(2, "domain", step2Errors.domain)} />
                </div>
                <div className="space-y-2">
                  <Label>Verticals *</Label>
                  <MultiSelectCheckboxTag
                    placeholder="Select verticals"
                    value={vertical}
                    onChange={setVertical}
                    options={verticalOptions.map((v) => ({
                      label: v,
                      value: v,
                    }))}
                    containerClassName="w-full"
                    showSelectAll
                  />
                  <FieldError message={fieldError(2, "vertical", step2Errors.vertical)} />
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <SingleSelect
                    options={typeOptions}
                    defaultValue={brandType}
                    onChange={setBrandType}
                    placeholder="Select type"
                    containerClassName="w-full"
                  />
                  <FieldError message={fieldError(2, "type", step2Errors.type)} />
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="rounded-md bg-muted/50 px-3 py-2 text-sm space-y-1">
                <div>
                  <span className="text-muted-foreground">Organization:</span>{" "}
                  <span className="font-medium">{selectedOrgLabel}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Brand:</span>{" "}
                  <span className="font-medium">{name || "—"}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Brand Features *</Label>
                <p className="text-xs text-muted-foreground">
                  Select at least one brand feature. Performance Creator is
                  separate (Grapevine).
                </p>
                <div className="flex flex-wrap gap-4 pt-1">
                  {WIZARD_PRODUCT_FEATURES.map(({ key, label }) => (
                    <div key={key} className="flex items-center space-x-2">
                      <LiftCheckbox
                        id={`wizard-product-${key}`}
                        checked={productFeatures.has(key)}
                        onCheckedChange={(c) =>
                          toggleProduct(key, c === true)
                        }
                      />
                      <Label
                        htmlFor={`wizard-product-${key}`}
                        className="font-normal cursor-pointer"
                      >
                        {label}
                      </Label>
                    </div>
                  ))}
                  <div className="flex items-center space-x-2">
                    <LiftCheckbox
                      id="wizard-product-pc"
                      checked={isPerformanceCreator}
                      onCheckedChange={(c) => {
                        const on = c === true;
                        setIsPerformanceCreator(on);
                        if (!on) setSlugTouched(false);
                      }}
                    />
                    <Label
                      htmlFor="wizard-product-pc"
                      className="font-normal cursor-pointer"
                    >
                      Performance Creator
                    </Label>
                  </div>
                </div>
                <FieldError message={fieldError(3, "features", step3Errors.features)} />
              </div>

              <div className="space-y-2">
                <Label>
                  HubSpot Company ID
                  {isPerformanceCreator ? " *" : ""}
                </Label>
                <Input
                  value={hubspotCompanyId}
                  onChange={(e) => setHubspotCompanyId(e.target.value)}
                  onBlur={() => markTouched("hubspot")}
                  placeholder="Optional for non–Performance Creator brands"
                />
                <FieldError
                  message={fieldError(3, "hubspot", step3Errors.hubspotCompanyId)}
                />
                <p className="text-xs text-muted-foreground">
                  {isPerformanceCreator
                    ? "Required when Performance Creator is enabled."
                    : "Optional. Many non-Grapevine brands still use HubSpot."}
                </p>
              </div>

              {isPerformanceCreator && (
                <div className="space-y-4 rounded-md border p-3">
                  <p className="text-sm font-medium">Grapevine (Performance Creator)</p>
                  <div className="space-y-2">
                    <Label>Slug *</Label>
                    <Input
                      value={slug}
                      onChange={(e) => {
                        setSlugTouched(true);
                        setSlug(e.target.value);
                      }}
                      onBlur={() => markTouched("slug")}
                      placeholder="From alias email"
                    />
                    <FieldError message={fieldError(3, "slug", step3Errors.slug)} />
                    <GrapevineSlugAvailabilityMessage
                      status={slugStatus}
                      slug={slug}
                      resolvedSlug={resolvedSlug}
                    />
                    <p className="text-xs text-muted-foreground">
                      Auto-filled from alias (
                      {slugFromAliasEmail(alias) || "enter alias on step 2"}).
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Grapevine vertical *</Label>
                    <Input
                      value={gvVertical}
                      onChange={(e) => setGvVertical(e.target.value)}
                      onBlur={() => markTouched("gvVertical")}
                    />
                    <FieldError
                      message={fieldError(3, "gvVertical", step3Errors.gvVertical)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact email *</Label>
                    <Input
                      type="email"
                      value={gvContactEmail}
                      onChange={(e) => setGvContactEmail(e.target.value)}
                      onBlur={() => markTouched("gvContactEmail")}
                    />
                    <FieldError
                      message={fieldError(
                        3,
                        "gvContactEmail",
                        step3Errors.gvContactEmail,
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact name</Label>
                    <Input
                      value={gvContactName}
                      onChange={(e) => setGvContactName(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <SheetFooter className="border-t pt-4 gap-2 sm:justify-between">
          <div>
            {step > 1 && (
              <Button type="button" variant="ghost" onClick={goBack}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            {step < 3 ? (
              <Button
                type="button"
                disabled={step === 1 && creatingOrg}
                onClick={() => goNext()}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="button"
                disabled={
                  saving || (isPerformanceCreator && slugStatus === "checking")
                }
                onClick={() => finish()}
              >
                {saving ? "Saving…" : "Save brand"}
              </Button>
            )}
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
